import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { Link, Typography } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ADIColors from "@NA/Utils/ADIColors";

import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showChangeLink = "showChangeLink",
}

interface CustomProps extends WidgetProps {
    fields: {
        [fields.showChangeLink]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = state.context.session;
    const billToId = session.billToId;
    const billToState = getBillToState(state, billToId).value;
    const changeCustomerPageUrl = getPageLinkByPageType(state, "ChangeCustomerPage")?.url;
    const pathName = getLocation(state).pathname;
    const shipToId = session.shipToId;
    const shipToState = getShipToState(state, shipToId).value;
    return {
        billToState,
        session,
        changeCustomerPageUrl,
        pathName,
        shipToState,
    };
};

type Props = WidgetProps & CustomProps & ReturnType<typeof mapStateToProps>;

export interface ADIChangeCustomerTopBarStyles {
    wrapper: InjectableCss;
    innerWrapper?: InjectableCss;
    customerNumber?: TypographyProps;
    changeLink?: LinkPresentationProps;
}

export const adiChangeCustomerTopBarStyles: ADIChangeCustomerTopBarStyles = {
    wrapper: {
        css: css`
            max-width: 1440px;
            margin: 0 auto;
            height: 44px;
            display: flex;
            background: rgba(124, 147, 173, 0.2);
            align-items: center;
            justify-content: center;
            border-radius: 4px;

            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                    margin: 0px;
                    `,
                    null,
                    null,
                ]);
            }}
        `,
    },
    innerWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        justify-content: space-around;
                        padding: 0 15px 0 12px;
                    `,
                    css`
                        width: 100%;
                        justify-content: space-around;
                        padding: 0 15px 0 12px;
                    `,
                    null,
                    null,
                    null,
                ]);
            }}
        `,
    },
    customerNumber: {
        css: css`
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            max-width: 700px;
            font-weight: 300;
            color: ${ADIColors.darkBlue5};
            text-transform: capitalize;
            font-size: 16px;
            line-height: 1.5em;
            > strong {
                margin-right:15px;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        max-width: initial;
                        font-size: 14px;
                    `,
                    css`
                        max-width: initial;
                        font-size: 14px;
                    `,
                    css`
                        max-width: 600px;
                    `,
                    null,
                    null,
                ]);
            }}
            @media print {
                overflow: unset;
                white-space: unset;
                text-overflow: unset;
                max-width: 100%;
                font-size: 12px;
                font-weight: 700;
                color: ${ADIColors.black};
            }
        `,
    },
    changeLink: {
        css: css`
            font-size: 16px;
            font-weight: 600;
            color: ${getColor("primary")} !important;
            text-transform: capitalize;
            margin-left: 12px;
            :hover {
                text-decoration: underline;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 14px;
                        white-space: nowrap;
                    `,
                    css`
                        font-size: 14px;
                        white-space: nowrap;
                    `,
                    null,
                    null,
                    null,
                ]);
            }}
            @media print {
                display: none;
            }
        `,
    },
};

const styles = adiChangeCustomerTopBarStyles;
const isPaymentRecapPage = typeof window !== 'undefined' && window.location.pathname.includes('InvoicePaymentRecap');

const ADIChangeCustomerTopBar: FC<Props> = ({ shipToState, session, changeCustomerPageUrl, pathName, billToState, fields }) => {
    return (
        <>
            {shipToState && (
                <StyledWrapper {...styles.wrapper}>
                    <StyledWrapper {...styles.innerWrapper}>
                        <Typography {...styles.customerNumber} as="div">
                            <strong>
                                {billToState?.customerNumber} | {billToState?.companyName}
                            </strong>
                            &nbsp;{shipToState?.address1} {shipToState?.city},{" "}
                            {shipToState?.state && shipToState?.state.name} {shipToState?.postalCode}
                        </Typography>
                        {session.displayChangeCustomerLink && (fields.showChangeLink || fields.showChangeLink === undefined) && !isPaymentRecapPage && (
                            <Link {...styles.changeLink} href={`${changeCustomerPageUrl}?returnurl=${pathName}`}>
                                {translate("Change")}
                            </Link>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ADIChangeCustomerTopBar),
    definition: {
        group: "My Account",
        displayName: "ADI ChangeCustomerTopBar",
        fieldDefinitions: [
            {
                name: fields.showChangeLink,
                displayName: "Show Change Link",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    }
};

export default widgetModule;
